// @givmo/app-charity — THE auth door: one unified chooser for sign-in AND account creation.
//
// One card, both routes. /login-home (the guard's default landing) and /sign-up (where the guard
// forwards a signed-out caller of an EMAILED deep link — the outreach email's `/claim/<id>`,
// D-CP-64, and the admin-invite email's `/invites/accept?token=…`, D-CP-65; neither email links to
// /sign-up itself) render THIS component with identical content — heading,
// subtitle, three buttons, nothing else. The former mode toggle ("New to…? Create an account" ⇄ "Already have an account?
// Sign in") swapped only the copy over an identical button set, which read as a broken control
// (2026-08-18 operator decision, from his own walkthrough). The toggle is gone: the heading names
// both verbs once, and the two routes stay only because arrivals differ (the emailed deep links'
// signed-out forward → /sign-up; other guard bounces and sign-out → /login-home).
//
// TWO WAYS IN, deliberately asymmetric:
//   - PROVIDERS ("Continue with Google/Microsoft") have true continue semantics. Firebase's provider
//     sign-in creates the account if it does not exist and signs in if it does, and this app makes no
//     backend registration call at sign-up (an admin's organization arrives via the CLAIM flow), so
//     one button honestly serves a new and a returning caller. A brand-new provider user lands in the
//     entry gate → claim wizard; an existing one lands wherever `redirect` points.
//     ⚠ Verification differs by provider: Google attests `email_verified` and passes straight
//     through; GCIP mints microsoft.com users with `emailVerified: false` (Microsoft does not
//     attest, and its raw email claim is spoofable — nOAuth), so the guard routes them through the
//     backend OTP once (OTP_REPAIRABLE_PROVIDERS in auth-context.tsx) before the portal proper.
//   - EMAIL cannot be one path: Firebase blocks account enumeration by design, so an email-first
//     "do you already have an account?" probe cannot be built honestly. The door instead picks the
//     likelier form for how the caller ARRIVED — /sign-up (the signed-out forward of the two
//     emailed deep links, the outreach email's `/claim/<id>` and the admin-invite email's
//     `/invites/accept?token=…`, D-CP-64/D-CP-65; nothing links here directly) opens the create
//     form; /login-home (other guard bounces, sign-out) opens the sign-in form — and each form
//     cross-links the other, carrying anything already typed (`email-handoff.ts`). A wrong default
//     costs one click; the definitive fork ("This email is already registered") is caught on the
//     create form and offered a prefilled sign-in.
//
// The chooser rail (2026-08-11 operator ruling) still binds: a heading, ONE subtitle, three
// buttons, no prose. The subtitle is the operator's work-email nudge — always visible now, because
// the address fork (a provider account is an email address too) is chosen HERE, and the outreach
// audience reads this card before any form. `funnel-door.test.tsx` pins the card by count and
// content on BOTH routes. Two additions are ruled (D-AS-48 2a and 6), each drawn only where it can
// work, so the card everyone else sees is still the three-button chooser: the way back in for
// someone their company locks out, a link at the card's foot (AS-WEB26), and "Continue with
// Organization SSO", the last way in (AS-WEB30).
//
// ⚠ IT IS ALSO THE PER-MODULE DOOR (CPA-WEB40 · OM-12). A module mounted in this console has its own
// sign-in address (`/donations/sign-in`), and the only two things that differ there are the BRANDING
// (the module's name and glyph, from the class table) and where sign-in LANDS (that module, not the
// landing rule's answer). Both arrive as props, so there is one door rather than one per module —
// see `module-sign-in-page.tsx` for the route that supplies them.
//
// The second-factor branch lives here because a "Continue with" serves returning callers, who can
// owe a TOTP code (AS-WEB1). The email forms create-or-authenticate one step later.

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, ProviderButton, testIdProp } from '@givmo/design-system';
import { secondFactorChallengeOf, type AdminProviderId, type TotpChallenge } from '@givmo/auth';
import {
  ESCAPE_HATCH_LINK_CHARITY,
  EscapeHatchEntryLink,
  OrganizationSsoButton,
  SignInDoorError,
  organizationSsoLabel,
  useOrganizationSsoAvailability,
} from '@givmo/passkeys';
import type { UserCredential } from 'firebase/auth';
import { useAuth } from '../../auth/auth-context';
import { AuthShell } from './auth-shell';
import { TotpChallengePrompt } from './totp-challenge';
import { useAuthError } from './use-auth-error';
import { AUTH_ROUTES, DEFAULT_AUTHENTICATED_ROUTE } from './auth-routes';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { SIGN_IN_LANDING_STATE } from '../../console/sidebar-frame';
import { SIGN_UP_SUBTITLE } from '../../lib/funnel-copy';

export interface AuthDoorProps {
  /** Per-route page testid — the guard tests key off `page-login-home`; a module door is its own. */
  testId: 'page-login-home' | 'page-sign-up' | 'page-module-sign-in';
  /** Where "Continue with Email" lands — the route-informed default form (see header). */
  emailTo: typeof AUTH_ROUTES.signIn | typeof AUTH_ROUTES.signUpEmail;
  /** Per-route email-button testid, kept stable for the router/guard tests. */
  emailTestId: 'auth-signin-email' | 'auth-signup-email';
  /**
   * Where signing in lands when the URL carries no `redirect`. Defaults to the root, which re-runs the
   * landing rule — right for the console's own doors, wrong for a module's: someone who arrived
   * through Donations is owed Donations.
   */
  defaultTarget?: string;
  /** The module whose door this is — its name and glyph. Absent for the console's own doors. */
  module?: { label: string; icon: ReactNode };
}

export function AuthDoor({
  testId,
  emailTo,
  emailTestId,
  defaultTarget = DEFAULT_AUTHENTICATED_ROUTE,
  module,
}: AuthDoorProps) {
  const { signInWithProvider } = useAuth();
  const navigate = useNavigate();
  // One component serves two routes, so the search read is non-strict by necessity.
  const { redirect } = useSearch({ strict: false }) as { redirect?: string };
  // Where this door is, for a sub-flow it opens to come back to (the way back in, AS-WEB26).
  const here = useLocation({ select: (location) => location.href });
  const { error, lockRefused, clearError, surface } = useAuthError();
  const [pendingProvider, setPendingProvider] = useState<AdminProviderId | null>(null);
  // The way back in for someone their company locks out (AS-WEB26), returning to this door.
  const openEscapeHatch = useCallback(
    () => void navigate({ to: AUTH_ROUTES.lostCompanyAccess, search: { back: here } }),
    [navigate, here],
  );
  const [challenge, setChallenge] = useState<TotpChallenge<UserCredential> | null>(null);

  // The target this door carries: the sanitized `redirect`, else the door's own default. A module's
  // door defaults to the module; the console's doors default to the root, which re-runs the landing
  // rule — an account that already exists goes to its target; a brand-new one has no organization,
  // so the portal-entry gate puts them in the claim flow. Either way the route decides.
  const target = sanitizeRedirectTarget(redirect) ?? defaultTarget;
  const goToDestination = useCallback(
    () => navigate({ to: target, state: SIGN_IN_LANDING_STATE }),
    [navigate, target],
  );

  // The email form must land the caller where THIS door would: the guard's intended target, or a
  // module door's module. The console's own doors carry nothing when there is no redirect, so their
  // forms keep their own root default.
  const emailSearch = useMemo(
    () =>
      redirect !== undefined
        ? { redirect }
        : module !== undefined
          ? { redirect: defaultTarget }
          : {},
    [redirect, module, defaultTarget],
  );

  // AS-WEB30: the organization door, last in the ways in and only where it can work here (D-AS-48
  // 6); its step lands where this door would, and its way back returns here.
  const ssoAvailable = useOrganizationSsoAvailability();
  const openOrganizationSso = useCallback(
    () =>
      void navigate({ to: AUTH_ROUTES.organizationSso, search: { ...emailSearch, back: here } }),
    [navigate, emailSearch, here],
  );
  const organizationSso = ssoAvailable
    ? { label: organizationSsoLabel('continue'), onOpen: openOrganizationSso }
    : undefined;

  const onProvider = useCallback(
    async (provider: AdminProviderId) => {
      clearError();
      setPendingProvider(provider);
      try {
        await signInWithProvider(provider);
        await goToDestination();
      } catch (thrown) {
        // The first factor was accepted and a second factor is now owed (AS-WEB1) — a state of this
        // continue, not a failure of it. Only a returning caller can be challenged.
        const pending = secondFactorChallengeOf<UserCredential>(thrown);
        if (pending !== null) {
          setChallenge(pending);
        } else {
          surface(thrown);
        }
      } finally {
        setPendingProvider(null);
      }
    },
    [signInWithProvider, goToDestination, clearError, surface],
  );

  if (challenge !== null) {
    return (
      <AuthShell testId={testId}>
        <TotpChallengePrompt
          challenge={challenge}
          onResolved={goToDestination}
          onCancel={() => setChallenge(null)}
          onUnlink={openEscapeHatch}
          organizationSso={organizationSso}
        />
      </AuthShell>
    );
  }

  const busy = pendingProvider !== null;

  return (
    <AuthShell testId={testId}>
      <div className="flex flex-col gap-4">
        {/* A module's door wears that module's glyph, in the same tinted square its square in the
            app grid uses — so the door a person opened and the tile they will see afterwards read as
            the same thing. The Givmo brand panel beside it is unchanged: this is still a Givmo
            account, and a door that dropped the brand to wear a module's would be a door people
            cannot place. */}
        {module !== undefined && (
          <span
            className="mb-8 flex size-44 items-center justify-center rounded-xl bg-neutral-100 text-icon"
            {...testIdProp('module-sign-in-brand')}
          >
            {module.icon}
          </span>
        )}
        <h1 className="font-sans text-s22 font-w600 tracking-givmo text-text-strong">
          {module !== undefined ? `Sign in to ${module.label}` : 'Sign in or create your account'}
        </h1>
        {/* The door's only advice, and it is deliberately in subtitle register — the address fork
            is chosen HERE (a provider account is an email address too), not two screens later. */}
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          {SIGN_UP_SUBTITLE}
        </p>
      </div>

      <div className="flex flex-col gap-12">
        {/* The email path leads as the one filled control, so the card has a single anchor rather
            than three equal-weight buttons. `redirect` rides along: the form must land the caller on
            the guard's intended target, not this screen's default. */}
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          disabled={busy}
          onClick={() => void navigate({ to: emailTo, search: emailSearch })}
          testId={emailTestId}
        >
          Continue with Email
        </Button>

        <div className="flex items-center gap-12 py-4" aria-hidden>
          <span className="h-px flex-1 bg-border-subtle" />
          <span className="font-sans text-s12 font-w500 tracking-normal text-text-secondary">
            or continue with
          </span>
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        {/* The ONE shared control (`@givmo/design-system`); the testids match the admin portal's
            so one e2e selector drives a provider on either door. */}
        <ProviderButton
          provider="google"
          label="Continue with Google"
          loading={pendingProvider === 'google'}
          disabled={busy}
          onClick={() => void onProvider('google')}
          testId="login-google-button"
        />
        <ProviderButton
          provider="microsoft"
          label="Continue with Microsoft"
          loading={pendingProvider === 'microsoft'}
          disabled={busy}
          onClick={() => void onProvider('microsoft')}
          testId="login-microsoft-button"
        />
        {/* AS-WEB30: nothing at all where company sign-in can't run here (frame W0). */}
        <OrganizationSsoButton
          verb="continue"
          available={ssoAvailable}
          disabled={busy}
          onOpen={openOrganizationSso}
        />
      </div>

      {/* AS-WEB27: the company lock's refusal is the notice with the way back in, never the red
          line; every other failure is the red line as before. The notice offers the organization
          door first, where this page does (frame B1). */}
      <SignInDoorError
        error={error}
        lockRefused={lockRefused}
        onUnlink={openEscapeHatch}
        organizationSso={organizationSso}
      />

      {/* AS-WEB26: the way back in for someone their company locks out, at the card's foot — only
          where a lock exists here (the link renders nothing anywhere else, so this card keeps its
          three ways in). The one exception the chooser rule admits (D-AS-48 2a,
          `funnel-door.test.tsx`). It steps aside while the notice carries the way back in. */}
      {!(lockRefused && error !== null) && (
        <EscapeHatchEntryLink className={ESCAPE_HATCH_LINK_CHARITY} onOpen={openEscapeHatch} />
      )}
    </AuthShell>
  );
}

/** The door at /login-home — email opens the sign-in form (guard bounces skew returning). */
export function LoginHomeDoor() {
  return (
    <AuthDoor
      testId="page-login-home"
      emailTo={AUTH_ROUTES.signIn}
      emailTestId="auth-signin-email"
    />
  );
}

/**
 * The door at /sign-up — email opens the create form: arrivals here skew new (a signed-out caller
 * of the outreach deep link or the admin-invite link is forwarded here).
 */
export function SignUpDoor() {
  return (
    <AuthDoor
      testId="page-sign-up"
      emailTo={AUTH_ROUTES.signUpEmail}
      emailTestId="auth-signup-email"
    />
  );
}
